import type { ClientOverview, Currency } from '../../types/api';
import { convertUSDToCurrency, convertUSDToCurrencyFormatted } from './currencyConversion';

/**
 * Get total Net P/L (retained earnings) for a specific unit from client overview
 * @param clientOverviewData - Array of ClientOverview data
 * @param unitIdNo - The unit ID number to filter by
 * @returns Total Net P/L (retained earnings) for the specified unit
 */
export function getTotalNetPLForUnit(
  clientOverviewData: ClientOverview[],
  unitIdNo: number
): number {
  if (!clientOverviewData || clientOverviewData.length === 0 || !unitIdNo) {
    return 0;
  }

  const unitOverview = clientOverviewData.find(
    (overview) => overview.unit_id_no === unitIdNo
  );

  return unitOverview?.retained_earnings_usd || 0;
}

/**
 * Get total Net P/L (retained earnings) for a specific unit with formatting
 * @param clientOverviewData - Array of ClientOverview data
 * @param unitIdNo - The unit ID number to filter by
 * @returns Formatted total Net P/L string
 */
export function getTotalNetPLForUnitFormatted(
  clientOverviewData: ClientOverview[],
  unitIdNo: number
): string {
  const total = getTotalNetPLForUnit(clientOverviewData, unitIdNo);
  
  if (total === 0) {
    return '0';
  }
  
  // Format with commas for thousands
  return total.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
}

/**
 * Get total Net P/L in a specific currency for a specific unit
 * @param clientOverviewData - Array of ClientOverview data
 * @param unitIdNo - The unit ID number to filter by
 * @param targetCurrencyCode - Target currency code
 * @param currencies - Array of currency data
 * @returns Total Net P/L value in target currency
 */
export function getTotalNetPLForUnitInCurrency(
  clientOverviewData: ClientOverview[],
  unitIdNo: number,
  targetCurrencyCode: string,
  currencies: Currency[]
): number {
  const totalUSD = getTotalNetPLForUnit(clientOverviewData, unitIdNo);
  return convertUSDToCurrency(totalUSD, targetCurrencyCode, currencies);
}

/**
 * Get total Net P/L in a specific currency for a specific unit with formatting
 * @param clientOverviewData - Array of ClientOverview data
 * @param unitIdNo - The unit ID number to filter by
 * @param targetCurrencyCode - Target currency code
 * @param currencies - Array of currency data
 * @returns Formatted total Net P/L string in target currency
 */
export function getTotalNetPLForUnitInCurrencyFormatted(
  clientOverviewData: ClientOverview[],
  unitIdNo: number,
  targetCurrencyCode: string,
  currencies: Currency[]
): string {
  const totalUSD = getTotalNetPLForUnit(clientOverviewData, unitIdNo);
  return convertUSDToCurrencyFormatted(totalUSD, targetCurrencyCode, currencies, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useCurrencySymbol: false
  });
}

